import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
    createWebhook,
    deleteWebhook,
    fetchWebhook,
    fetchWebhookDeliveries,
    fetchWebhooks,
    sendTestWebhook,
} from './api'
import { notification } from 'antd'
import { t } from 'i18next'
import { StoreInput } from './input/store-input'
import { SearchQuery } from '../zod/pagination'

export function useWebhooks({ query, enabled = true }: { query?: SearchQuery; enabled?: boolean }) {
    return useQuery({
        queryFn: () => fetchWebhooks(query),
        queryKey: ['webhooks', query],
        enabled,
    })
}

export function useWebhook(webhookId: string, enabled = true) {
    return useQuery({
        queryFn: () => fetchWebhook(webhookId),
        queryKey: ['webhook', webhookId],
        enabled: enabled && webhookId.length > 0,
    })
}

export function useWebhookDeliveries({
    webhookId,
    query,
    enabled = true,
}: {
    webhookId: string
    query?: SearchQuery
    enabled?: boolean
}) {
    return useQuery({
        queryFn: () => fetchWebhookDeliveries({ webhookId, query }),
        queryKey: ['webhook-deliveries', webhookId, query],
        enabled: enabled && webhookId.length > 0,
    })
}

export function useCreateWebhook() {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: (input: StoreInput) => createWebhook(input),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['webhooks'] })
        },
    })
}

export function useDeleteWebhook() {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: (webhookId: string) => deleteWebhook(webhookId),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['webhooks'] })
            notification.success({
                message: t('Webhook deleted'),
                description: t('The webhook has been deleted successfully'),
            })
        },
    })
}

export function useSendTestWebhook() {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: (webhookId: string) => sendTestWebhook(webhookId),
        onSuccess: (delivery, webhookId) => {
            queryClient.invalidateQueries({ queryKey: ['webhooks'] })
            queryClient.invalidateQueries({ queryKey: ['webhook', webhookId] })
            queryClient.invalidateQueries({ queryKey: ['webhook-deliveries', webhookId] })

            if (delivery.status === 'success') {
                notification.success({
                    message: t('Test event sent'),
                    description:
                        delivery.httpStatus != null
                            ? t('The test event was delivered successfully (HTTP {{status}}).', {
                                  status: delivery.httpStatus,
                              })
                            : t('The test event was delivered successfully.'),
                })

                return
            }

            notification.error({
                message: t('Test event failed'),
                description: delivery.errorMessage ?? t('The test event could not be delivered.'),
            })
        },
        onError: () => {
            notification.error({
                message: t('Test event failed'),
                description: t('The test event could not be delivered.'),
            })
        },
    })
}
